import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  PamGrid,
  useNotification,
  useRowSelection,
  ConfirmationDialog,
  formatDateTimeForDisplay,
  GridConfigMenu,
  useGridConfigs,
  notifyPermissionError,
} from "@1823-partners/core";
import { SetFilterModule } from "ag-grid-enterprise";

import api, { fetchSecurityXref, saveSecurityXref, deleteSecurityXref } from "../api";

const CUSTODIAN_OPTIONS = ['CITI', 'JPM', 'NT', 'US BANK'];

// Row ID generator — logical key is custodian + custody identifier
const getCrosswalkRowId = (data) =>
  `${data.CUSTODIAN || ''}_${data.CUSTODY_IDENTIFIER || ''}`;

const EMPTY_FORM = {
  CUSTODIAN: '',
  CUSTODY_IDENTIFIER: '',
  CLEARWATER_IDENTIFIER: '',
  NOTE: '',
};

const Crosswalk = ({ userId, height = 500, refreshKey = 0 }) => {
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const gridRef = useRef(null);
  const { showNotification } = useNotification();

  // selectedRows is a Set keyed by getRowId — required by PamGrid's native
  // showSelectionColumn (it calls .has()).
  const {
    selectedRows,
    setSelectedRows,
    clearSelection,
    selectedCount,
    getSelectedData,
  } = useRowSelection({ getRowId: getCrosswalkRowId });

  const loadEntries = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchSecurityXref();
      const formattedData = Array.isArray(response.data) ? response.data : [];
      setRowData(formattedData);
      clearSelection();
    } catch (error) {
      console.error("Error fetching security crosswalk:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to load security crosswalk. Please try again.", 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [clearSelection, showNotification]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Refresh when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      loadEntries();
    }
  }, [refreshKey, loadEntries]);

  const columnDefs = useMemo(() => [
    { field: 'CUSTODIAN', headerName: 'Custodian', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CUSTODY_IDENTIFIER', headerName: 'Custody Identifier', editable: false, filter: 'agTextColumnFilter' },
    { field: 'CLEARWATER_IDENTIFIER', headerName: 'Clearwater Identifier', editable: false, filter: 'agTextColumnFilter' },
    { field: 'NOTE', headerName: 'Note', editable: false, filter: 'agTextColumnFilter', minWidth: 200, flex: 1 },
    { field: 'MODIFIED_BY', headerName: 'Modified By', editable: false, filter: 'agSetColumnFilter' },
    {
      field: 'MODIFIED_DATE',
      headerName: 'Modified Date',
      editable: false,
      type: 'date',
      filter: 'agDateColumnFilter',
      valueFormatter: (params) => params.value ? formatDateTimeForDisplay(params.value) : params.value,
    },
  ], []);

  // --- Delete flow -------------------------------------------------------

  const handleDeleteClick = useCallback(() => {
    if (selectedCount === 0) {
      showNotification('Please select at least one row to delete', 'warning');
      return;
    }
    setDeleteDialogOpen(true);
  }, [selectedCount, showNotification]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setIsDeleting(true);
      setDeleteDialogOpen(false);
      const deletions = getSelectedData(rowData).map(row => ({
        CUSTODIAN: row.CUSTODIAN,
        CUSTODY_IDENTIFIER: row.CUSTODY_IDENTIFIER,
        CLEARWATER_IDENTIFIER: row.CLEARWATER_IDENTIFIER,
      }));
      await deleteSecurityXref(deletions);
      clearSelection();
      showNotification(`Successfully deleted ${deletions.length} mapping(s)`, 'success');
    } catch (error) {
      console.error("Error deleting crosswalk mappings:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to delete crosswalk mappings. Please try again.", 'error');
      }
    } finally {
      setIsDeleting(false);
      await loadEntries();
    }
  }, [getSelectedData, rowData, clearSelection, loadEntries, showNotification]);

  // --- Add / Edit modal --------------------------------------------------

  const openAddModal = useCallback(() => {
    setEditingRow(null);
    setFormValues(EMPTY_FORM);
    setFormErrors({});
    setMappingDialogOpen(true);
  }, []);

  const openEditModal = useCallback((row) => {
    setEditingRow(row);
    setFormValues({
      CUSTODIAN: row.CUSTODIAN || '',
      CUSTODY_IDENTIFIER: row.CUSTODY_IDENTIFIER || '',
      CLEARWATER_IDENTIFIER: row.CLEARWATER_IDENTIFIER || '',
      NOTE: row.NOTE || '',
    });
    setFormErrors({});
    setMappingDialogOpen(true);
  }, []);

  const handleEditClick = useCallback(() => {
    if (selectedCount !== 1) {
      showNotification('Select exactly one row to edit', 'warning');
      return;
    }
    openEditModal(getSelectedData(rowData)[0]);
  }, [selectedCount, getSelectedData, rowData, openEditModal, showNotification]);

  const handleFormChange = useCallback((field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!formValues.CUSTODIAN) errors.CUSTODIAN = 'Custodian is required';
    if (!formValues.CUSTODY_IDENTIFIER?.trim()) errors.CUSTODY_IDENTIFIER = 'Custody Identifier is required';
    if (!formValues.CLEARWATER_IDENTIFIER?.trim()) errors.CLEARWATER_IDENTIFIER = 'Clearwater Identifier is required';
    return errors;
  }, [formValues]);

  const handleMappingSubmit = useCallback(async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setIsSaving(true);
      const row = {
        CUSTODIAN: formValues.CUSTODIAN,
        CUSTODY_IDENTIFIER: formValues.CUSTODY_IDENTIFIER.trim(),
        CLEARWATER_IDENTIFIER: formValues.CLEARWATER_IDENTIFIER.trim(),
        NOTE: formValues.NOTE?.trim() || '',
      };
      await saveSecurityXref([row]);
      const verb = editingRow ? 'updated' : 'added';
      showNotification(`Successfully ${verb} mapping`, 'success');
      setMappingDialogOpen(false);
      await loadEntries();
    } catch (error) {
      console.error("Error saving crosswalk mapping:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to save mapping. Please try again.", 'error');
      }
    } finally {
      setIsSaving(false);
    }
  }, [formValues, editingRow, validateForm, loadEntries, showNotification]);

  const handleMappingClose = useCallback(() => {
    setMappingDialogOpen(false);
    setFormErrors({});
  }, []);

  // --- Grid configs + toolbar -------------------------------------------

  const gridConfigs = useGridConfigs(api, {
    app: 'recon',
    gridId: 'crosswalk',
    gridApiRef: gridRef,
  });

  const addMappingButton = useMemo(() => (
    <Button
      key="add-mapping"
      variant="contained"
      size="small"
      onClick={openAddModal}
      data-testid="add-mapping-button"
    >
      Add Mapping
    </Button>
  ), [openAddModal]);

  const editMappingButton = useMemo(() => (
    <Button
      key="edit-mapping"
      variant="outlined"
      size="small"
      onClick={handleEditClick}
      disabled={selectedCount !== 1}
      data-testid="edit-mapping-button"
    >
      Edit Mapping
    </Button>
  ), [handleEditClick, selectedCount]);

  const customToolbarComponents = useMemo(() => [
    addMappingButton,
    editMappingButton,
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [addMappingButton, editMappingButton, gridConfigs]);

  return (
    <Box sx={{ height: `${Math.max(height - 100, 300)}px` }}>
      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        getRowId={getCrosswalkRowId}
        height={`${Math.max(height - 200, 200)}px`}
        customToolbarComponents={customToolbarComponents}
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
        // Toolbar
        showToolbar
        title="Security Crosswalk"
        showExport
        exportConfig={{
          fileName: `SecurityCrosswalk_${new Date().toISOString().split('T')[0]}.xlsx`,
          sheetName: 'Security Crosswalk',
        }}
        showBulkDelete
        onBulkDelete={handleDeleteClick}
        selectedRowCount={selectedCount}
        // Selection column
        showSelectionColumn
        selectedRows={selectedRows}
        onRowSelectionChange={setSelectedRows}
        isSelectionDisabled={isLoading || isDeleting}
        // Quick Filter
        showQuickFilter
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        quickFilterPlaceholder="Quick Filter..."
        // State
        isLoading={isLoading}
        loadingMessage="Loading security crosswalk..."
        // Additional modules
        modules={[SetFilterModule]}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Mappings"
        message={`Are you sure you want to delete ${selectedCount} mapping(s)? This action cannot be undone.`}
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        confirmColor="error"
      />

      {/* Add / Edit Mapping Dialog */}
      <Dialog
        open={mappingDialogOpen}
        onClose={handleMappingClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="mapping-dialog-title"
      >
        <DialogTitle id="mapping-dialog-title" data-testid="mapping-dialog-title">
          {editingRow ? 'Edit Mapping' : 'Add Mapping'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth error={!!formErrors.CUSTODIAN}>
              <InputLabel id="custodian-label">Custodian *</InputLabel>
              <Select
                labelId="custodian-label"
                label="Custodian *"
                value={formValues.CUSTODIAN}
                onChange={(e) => handleFormChange('CUSTODIAN', e.target.value)}
                inputProps={{ 'data-testid': 'custodian-select' }}
              >
                {CUSTODIAN_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
              {formErrors.CUSTODIAN && (
                <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }} data-testid="custodian-error">
                  {formErrors.CUSTODIAN}
                </Box>
              )}
            </FormControl>

            <TextField
              label="Custody Identifier *"
              value={formValues.CUSTODY_IDENTIFIER}
              onChange={(e) => handleFormChange('CUSTODY_IDENTIFIER', e.target.value)}
              error={!!formErrors.CUSTODY_IDENTIFIER}
              helperText={formErrors.CUSTODY_IDENTIFIER}
              fullWidth
              inputProps={{ 'data-testid': 'custody-identifier-input' }}
            />

            <TextField
              label="Clearwater Identifier *"
              value={formValues.CLEARWATER_IDENTIFIER}
              onChange={(e) => handleFormChange('CLEARWATER_IDENTIFIER', e.target.value)}
              error={!!formErrors.CLEARWATER_IDENTIFIER}
              helperText={formErrors.CLEARWATER_IDENTIFIER}
              fullWidth
              inputProps={{ 'data-testid': 'clearwater-identifier-input' }}
            />

            <TextField
              label="Note"
              value={formValues.NOTE}
              onChange={(e) => handleFormChange('NOTE', e.target.value)}
              multiline
              rows={3}
              fullWidth
              inputProps={{ 'data-testid': 'note-input' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMappingClose} data-testid="mapping-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleMappingSubmit}
            variant="contained"
            disabled={isSaving}
            data-testid="mapping-submit-button"
          >
            {isSaving ? 'Saving...' : editingRow ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Crosswalk;
